// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — features/financeiro/components/ColunaOrdenavel.tsx
//
//  Cabecalho de coluna clicavel para as grids financeiras.
//
//  A ordenacao acontece no SERVIDOR (as listas de Contas a Pagar/Receber sao
//  paginadas la): este componente so reporta qual campo e qual direcao. Ordenar
//  no cliente ordenaria apenas as ~20 linhas da pagina corrente.
// ════════════════════════════════════════════════════════════════════════════

import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { DirecaoOrdenacao } from "../types";

interface ColunaOrdenavelProps<T extends string> {
  /** Campo enviado ao backend (precisa estar na whitelist do controller). */
  campo: T;
  /** Campo atualmente ordenado. */
  ordenarPor: T;
  direcao: DirecaoOrdenacao;
  /** Alterna: mesmo campo inverte a direcao; campo novo entra na direcao padrao. */
  onOrdenar: (campo: T) => void;
  children: React.ReactNode;
  className?: string;
  /** Alinha o conteudo a direita (colunas de valor). */
  alinharDireita?: boolean;
}

export function ColunaOrdenavel<T extends string>({
  campo, ordenarPor, direcao, onOrdenar, children, className, alinharDireita,
}: ColunaOrdenavelProps<T>) {
  const ativa = ordenarPor === campo;

  return (
    <TableHead className={cn(alinharDireita && "text-right", className)}>
      <button
        type="button"
        onClick={() => onOrdenar(campo)}
        aria-label={`Ordenar por ${typeof children === "string" ? children : campo}`}
        // aria-sort vive no <th>, mas o Radix TableHead nao repassa: o estado
        // tambem vai no title/icone para leitores de tela e para o olho.
        title={ativa
          ? `Ordenado por ${campo} (${direcao === "asc" ? "crescente" : "decrescente"}) — clique para inverter`
          : `Ordenar por ${campo}`}
        className={cn(
          "inline-flex items-center gap-1 select-none transition-colors hover:text-foreground",
          alinharDireita && "flex-row-reverse",
          ativa ? "font-semibold text-foreground" : "text-muted-foreground",
        )}
      >
        {children}
        {!ativa
          ? <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" aria-hidden />
          : direcao === "asc"
            ? <ArrowUp className="h-3.5 w-3.5" aria-hidden />
            : <ArrowDown className="h-3.5 w-3.5" aria-hidden />}
      </button>
    </TableHead>
  );
}
